import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerStatsQuery, SupplierStatsQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const supplierStatsQuery = graphql(`
  query supplierStats($organisationId: String!, $includeSignals: Boolean!) {
    supplierStats(organisationId: $organisationId) {
      awardsCount
      mentionsCount
      expiriesCount
      keyAccountsCount
      signals @include(if: $includeSignals) {
        id
        name
        category
        colour
      }
    }
  }
`);

/**
 * Returns organisation stats (awards, mentions, expiries, saved buyers) for supplier header cards.
 * Signals cost a live notice aggregation server-side, so callers that won't render them ask for
 * `includeSignals: false` and the field is left out of the selection set entirely.
 */
export function useSupplierStats(
  organisationId: string | undefined,
  { includeSignals }: { includeSignals: boolean },
  options?: UseQueryOptions<SupplierStatsQuery, unknown, SupplierStatsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery<SupplierStatsQuery>(
    ["supplierStatsOrganisation", organisationId, includeSignals],
    supplierStatsQuery,
    [{ organisationId, includeSignals }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: !!organisationId && (options?.enabled ?? true),
      ...options,
    },
  );

  return {
    data: data?.supplierStats,
    ...rest,
  };
}

const buyerStatsQuery = graphql(`
  query buyerStats($organisationId: String!) {
    buyerStats(organisationId: $organisationId) {
      allNoticesCount
      openOpportunitiesCount
      upcomingExpiriesCount
    }
  }
`);

/**
 * Returns buyer stats (all notices, open opportunities, upcoming expiries) for buyer header cards.
 */
export function useBuyerStats(
  organisationId: string | undefined,
  options?: UseQueryOptions<BuyerStatsQuery, unknown, BuyerStatsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery<BuyerStatsQuery>(
    ["buyerStatsGql", organisationId ?? ""],
    buyerStatsQuery,
    [{ organisationId: organisationId ?? "" }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: !!organisationId,
      ...options,
    },
  );

  return {
    data: data?.buyerStats,
    ...rest,
  };
}
